import type {
  WorkOrderAttemptCreateRequest,
  WorkOrderAttemptUpdateRequest,
} from '@/types/workOrderAttempt.types';
import type { HiAttemptUpdatePayload } from '@/pages/homeInspection/hiAttempt.schema';

export type PsFirstAttemptHiEditSaveDeps = {
  patchPsAttempt: (args: {
    workOrderId: string;
    data: {
      create?: WorkOrderAttemptCreateRequest[];
      update?: WorkOrderAttemptUpdateRequest[];
    };
  }) => Promise<unknown>;
  updateHiAttempt: (
    hiWorkOrderId: string,
    hiAttemptId: string,
    payload: HiAttemptUpdatePayload
  ) => Promise<unknown>;
};

export type PsFirstAttemptHiEditSaveInput = {
  psWorkOrderId: string;
  linkedHIWorkOrderID: string | null | undefined;
  hiAttemptId: string;
  psUpdatePayload: WorkOrderAttemptUpdateRequest;
  hiUpdatePayload: HiAttemptUpdatePayload;
  /** After a prior HI-success / PS-failure, retry Process Service only. */
  psOnly?: boolean;
};

export type PsFirstAttemptHiEditSaveResult =
  | { ok: true }
  | {
      ok: false;
      failedAt: 'missingLinkedHi' | 'hi' | 'psAfterHi';
      error: unknown;
    };

/**
 * Edit first PS+HI attempt: HI PATCH first, then PS PATCH update[].
 * HI-first avoids committing PS when HI validation fails. If PS fails after HI,
 * callers must surface a partial-failure message (failedAt: 'psAfterHi') and may
 * retry with `psOnly: true` so HI is not patched again.
 */
export const savePsFirstAttemptHiEdit = async (
  input: PsFirstAttemptHiEditSaveInput,
  deps: PsFirstAttemptHiEditSaveDeps
): Promise<PsFirstAttemptHiEditSaveResult> => {
  if (!input.linkedHIWorkOrderID) {
    return {
      ok: false,
      failedAt: 'missingLinkedHi',
      error: new Error('Linked Home Inspection work order is missing'),
    };
  }

  if (!input.psOnly) {
    try {
      await deps.updateHiAttempt(input.linkedHIWorkOrderID, input.hiAttemptId, input.hiUpdatePayload);
    } catch (error) {
      return { ok: false, failedAt: 'hi', error };
    }
  }

  try {
    await deps.patchPsAttempt({
      workOrderId: input.psWorkOrderId,
      data: {
        create: [],
        update: [input.psUpdatePayload],
      },
    });
  } catch (error) {
    return { ok: false, failedAt: 'psAfterHi', error };
  }

  return { ok: true };
};
